import { TFunction } from 'i18next';

/**
 * Get display name for project type with internationalization support
 */
export const getProjectTypeDisplayName = (type: string, t: TFunction): string => {
    switch (type) {
        case 'containeryard':
        case 'container_yard':
            return t('container_yard') || 'Container Yard';
        case 'parkingdeck':
        case 'parking_deck':
            return t('parking_deck') || 'Parking Deck';
        case 'unpaved':
            return t('unpaved_road') || 'Unpaved Road';
        case 'paved':
            return t('paved') || 'Paved';
        case 'geogrid':
            return t('geogrid') || 'Geogrid Studio';
        case 'railways':
            return t('railway') || 'Railway';
        case 'airport':
            return t('airport') || 'Airport';
        default:
            return t('project') || 'Project';
    }
};

/**
 * Get standardized logo path for PDF documents
 */
export const getLogoPath = (): string => {
    return '/images/logoMaccaferri.png';
};

/**
 * Get appropriate unit based on unit system
 */
export const getUnit = (metricUnit: string, imperialUnit: string, unitSystem: string): string => {
    return unitSystem === 'Metric' ? metricUnit : imperialUnit;
};

/**
 * Format PDF values consistently across all components
 * Handles null, undefined, empty values and provides consistent number formatting
 */
export const formatPdfValue = (value: unknown, precision = 2): string => {
    if (value === null || value === undefined || value === '') return '-';

    if (typeof value === 'number') {
        return Number.isInteger(value) ? value.toString() : value.toFixed(precision);
    }

    return String(value);
};

/**
 * Safe text conversion with fallback
 */
export const safeText = (value: unknown, fallback = '-'): string => {
    return value?.toString() ?? fallback;
};

/**
 * Get localized "not available" text
 */
export const getNotAvailableText = (t: TFunction): string => {
    return t('common.not_available', 'N/A');
};

/**
 * Format date for PDF display
 */
export const formatPdfDate = (date?: Date | string | null): string => {
    if (!date) return new Date().toLocaleDateString();

    if (typeof date === 'string') {
        const parsedDate = new Date(date);
        return isNaN(parsedDate.getTime()) ? new Date().toLocaleDateString() : parsedDate.toLocaleDateString();
    }

    return date.toLocaleDateString();
};

/**
 * Get safe property value from nested objects
 */
export const getSafeProperty = (obj: Record<string, unknown>, path: string, fallback = '-'): string => {
    const keys = path.split('.');
    let current: unknown = obj;

    for (const key of keys) {
        if (current && typeof current === 'object' && key in current) {
            current = (current as Record<string, unknown>)[key];
        } else {
            return fallback;
        }
    }

    return safeText(current, fallback);
};
